import { ImagePlus, Trash2 } from 'lucide-react'
import { useEffect, useId, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import ProjectMark, { projectIcons } from '@/frontend/features/Fde/ProjectMark'
import { cn } from '@/lib/utils'
import type { FdeCopy } from '@/frontend/features/Fde/types'

interface Props {
  name: string
  icon: string
  logoUrl: string | null
  logo: File | null
  removed: boolean
  copy: FdeCopy
  onIcon: (value: string) => void
  onLogo: (file: File | null, removed: boolean) => void
}

export default function ProjectIdentityFields(props: Props) {
  const logoInputId = useId()
  const [preview, setPreview] = useState<string | null>(props.removed ? null : props.logoUrl)

  useEffect(() => {
    if (!props.logo) {
      setPreview(props.removed ? null : props.logoUrl)
      return
    }
    const objectUrl = URL.createObjectURL(props.logo)
    setPreview(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [props.logo, props.logoUrl, props.removed])

  return (
    <div className="grid gap-3 rounded-xl border border-border bg-muted/15 p-4">
      <div className="flex items-center gap-3">
        <ProjectMark project={{ name: props.name, icon: props.icon, logo_url: preview }} className="size-16 rounded-xl bg-background" />
        <div className="min-w-0 flex-1">
          <Label htmlFor={logoInputId}>{props.copy.forms.project_logo}</Label>
          <input id={logoInputId} className="sr-only" type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => props.onLogo(event.target.files?.[0] || null, false)} />
          <div className="mt-1.5"><Button asChild size="sm" variant="outline"><label htmlFor={logoInputId} className="cursor-pointer"><ImagePlus />{props.copy.forms.choose_project_logo}</label></Button></div>
          <p className="mt-1 text-[11px] text-muted-foreground">{props.copy.forms.project_logo_hint}</p>
        </div>
        {preview && <Button type="button" size="icon-sm" variant="ghost" onClick={() => props.onLogo(null, true)} aria-label={props.copy.forms.remove_project_logo}><Trash2 /></Button>}
      </div>
      {!preview && <div>
        <Label>{props.copy.forms.project_icon}</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {Object.entries(projectIcons).map(([value, Icon]) => <button key={value} type="button" onClick={() => props.onIcon(value)} className={cn('flex size-9 items-center justify-center rounded-lg border bg-background text-muted-foreground transition-colors hover:text-foreground', props.icon === value && 'border-primary bg-primary/8 text-primary ring-2 ring-primary/15')} aria-label={value}><Icon className="size-4" /></button>)}
        </div>
      </div>}
      {!preview && <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><ImagePlus className="size-3" />{props.copy.forms.project_identity_hint}</p>}
    </div>
  )
}
